import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { Field } from 'formik';
import TextField from '../InputFields/TextField';
import { InputFieldWrapper } from '../InputFields/Components/InputComponents';
import config from '../../config';

const GOOGLE_LIBRARIES = ['places'];

const LocationSearchField = ({ onPlaceSelected, restrictToUs }) => {
  const { t } = useTranslation();
  const autocompleteRef = useRef(null);
  const [authFailed, setAuthFailed] = useState(false);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: config.googleMaps.apiKey,
    libraries: GOOGLE_LIBRARIES,
  });

  useEffect(() => {
    const previous = window.gm_authFailure;
    window.gm_authFailure = () => {
      setAuthFailed(true);
      if (typeof previous === 'function') previous();
    };
    return () => {
      window.gm_authFailure = previous;
    };
  }, []);

  const handlePlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place || !place.address_components) return;
    onPlaceSelected(place);
  }, [onPlaceSelected]);

  if (loadError || authFailed || !isLoaded) return null;

  return (
    <InputFieldWrapper>
      <Autocomplete
        onLoad={(autocomplete) => {
          autocompleteRef.current = autocomplete;
        }}
        onPlaceChanged={handlePlaceChanged}
        options={{
          componentRestrictions: restrictToUs ? { country: 'us' } : {},
          fields: ['address_components', 'geometry', 'formatted_address'],
          types: ['address'],
        }}
      >
        <Field
          component={TextField}
          name="location"
          autocomplete="street-address"
          placeholder={t('buyWizard.searchYourLocation')}
        />
      </Autocomplete>
    </InputFieldWrapper>
  );
};

LocationSearchField.propTypes = {
  onPlaceSelected: PropTypes.func.isRequired,
  restrictToUs: PropTypes.bool,
};

export default LocationSearchField;
